import { useCallback, useEffect } from "react";
import { useRecoilState, useResetRecoilState } from "recoil";
import {
  displayRequestsAtom,
  filterParamAtom,
  requestsFromRemoteAtom,
  sortParamAtom,
} from "../utils/requestAtoms";
import {
  ERequestSubtype,
  ERequestStatus,
  ERequestType,
  INotificationRequest,
  IUser,
} from "../../../common-interfaces/interfaces";
import { useOnGet } from "../../../hooks/useOnGet";
import { useApiWrapper } from "../../../hooks/useApiWrapper";
import { URLS } from "../../../helpers/URLS";
import { PrintError } from "../../../helpers/utils";
import { userProfileAtom } from "../../../recoil/atoms";

export default function useRequestNotificationUtils() {
  const [remoteRequests, setRemoteRequests] = useRecoilState<INotificationRequest[]>(requestsFromRemoteAtom);
  const [, setDisplayRequests] = useRecoilState(displayRequestsAtom);
  const [filterParam] = useRecoilState(filterParamAtom);
  const [user] = useRecoilState<IUser | undefined>(userProfileAtom);

  const GET = useOnGet();
  const { postWithAuth, deleteWithAuth, putWithAuth } = useApiWrapper();
  const resetSortParam = useResetRecoilState(sortParamAtom);

  /**
   * Combina filtraggio e ordinamento e aggiorna lo stato una sola volta.
   */
  const updateDisplayRequests = useCallback(() => {
    if (!user?.username) return;
    // Filtraggio: usa il filtro corrente, per default "all"
    const filtered = remoteRequests.filter((req) => {
      if (filterParam === "all") {
        return req.from === user.username || req.to === user.username;
      }
      return req[filterParam] === user.username;
    });

    setDisplayRequests(filtered);
  }, [
    remoteRequests,
    user?.username,
    filterParam,
    setDisplayRequests,
  ]);

  useEffect(() => {
    updateDisplayRequests();
  }, [remoteRequests, user, filterParam, updateDisplayRequests]);


  const getPendingRequestsReceived = useCallback((): INotificationRequest[] => {
    if (!user?.username) return [];
    return remoteRequests.filter(
      (req) => req.to === user.username && req.status === ERequestStatus.PENDING
    );
  }, [remoteRequests, user?.username]);

  const getPendingRequestsSent = useCallback((): INotificationRequest[] => {
    if (!user?.username) return [];
    return remoteRequests.filter(
      (req) => req.from === user.username && req.status === ERequestStatus.PENDING
    );
  }, [remoteRequests, user?.username]);

  const getAllPendingRequests = useCallback((): INotificationRequest[] => {
    if (!user?.username) return [];
    return remoteRequests.filter(
      (req) =>
        req.status === ERequestStatus.PENDING &&
        (req.from === user.username || req.to === user.username)
    );
  }, [remoteRequests, user?.username]);

  const shareNewRequest = async (
    to: string,
    type: ERequestType,
    subtype: ERequestSubtype,
    idObject: string,
    label: string
  ) => {
    if (!user?.username) return;
    const newRequest = {
      status: ERequestStatus.PENDING,
      from: user.username,
      to,
      type,
      subtype,
      idObject,
      label,
      subrequests: [],
    };
    try {
      await postWithAuth(URLS.request, newRequest);
    } catch (error) {
      PrintError([error]);
    }
  };

  const deleteRequest = async (reqID: string) => {
    try {
      await deleteWithAuth(`${URLS.request}/${reqID}`, {});
      setDisplayRequests((prev) => prev.filter((req) => req._id !== reqID));
      setRemoteRequests((prev) => prev.filter((req) => req._id !== reqID));
    } catch (error) {
      PrintError([error]);
    }
  };

  const updateRequestStatus = async (req: INotificationRequest, status: ERequestStatus) => {
    const updatedRequest = { ...req, status };
    try {
      await putWithAuth(`${URLS.request}/${req._id}`, updatedRequest);
      setRemoteRequests((prev) =>
        prev.map((r) => (r._id === updatedRequest._id ? updatedRequest : r))
      );
    } catch (error) {
      PrintError([error]);
    }
  };

  useEffect(() => {
    if (!user?.username) GET.user();
  }, [user, GET]);

  return {
    updateDisplayRequests,
    getPendingRequestsReceived,
    getPendingRequestsSent,
    getAllPendingRequests,
    shareNewRequest,
    resetSortParam,
    deleteRequest,
    updateRequestStatus,
  };
}
